import { memo, FocusEvent, useRef, useEffect, useState, useMemo, ChangeEvent } from 'react';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../context/Keyboard/keyboard.types';
import { useLocale } from '../../../../context/Localization/localization';
import { useField, useFormikContext } from 'formik';
import { useKeyboardIntegration } from '../../../../context/Keyboard/keyboard';
import { InputMask, Masked, MaskedDynamic, MaskedDynamicOptions } from 'imask';
import { useIMask } from 'react-imask';
import { KioskFormPhoneOrEmailFieldProps, NumberMap } from '../FormPhoneOrEmailField.types';
import { PhoneOrEmailFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const KioskFormPhoneOrEmailField = memo(({
    name,
    id,
    title,
    label,
    ariaLabel,
    disabled,
    required,
    autoFocus,
    keyboardLayout,
    shiftAfterSpace,
    shiftAfterPeriod,
    shiftAfterAmpersand,
    hideKeyboardOnClickOut,
    capsLock,
    fullWidth,
    seventyFivePercentWidth,
    tabFocusRefContext,
    initiallyMasked,
    onChangeCallback,
}: KioskFormPhoneOrEmailFieldProps) => {
    const [field, meta] = useField<string>({
        name: name,
        validate: PhoneOrEmailFieldValidation({
            required: required ?? false,
            label,
            name,
        }),
    });
    const { submitCount, setFieldValue } = useFormikContext();

    /**
     * A ref to the HTMLInputElement in the MUI TextField.
     */
    const inputRef = useRef<HTMLInputElement>(null);
    /**
     * The input value ref to prevent a stale state when the on-screen keyboard
     * calls any functions that use the internal value state.
     */
    const valueRef = useRef<string>('');
    /**
     * The caret position ref to prevent a stale state when the on-screen keyboard
     * calls any functions that use the internal caret position state.
     */
    const caretPositionRef = useRef<number | null>(null);
    /**
     * The focus ref to prevent a stale state when the on-screen keyboard
     * calls any functions that need to know if the field is focused or not.
     */
    const isFocused = useRef<boolean>(false);
    /**
     * The on-screen keyboard input ref is used to prevent a stale state when we call to update it
     * from keyboard context functions. It is used to prevent the onChange function that recieves a
     * callback from the pattern mask from resetting the caret position incorrectly after we set it
     * using the keyboard context setValue function.
     */
    const wasOSKInput = useRef<boolean>(false);
    /**
     * This ref handles when the selection range only targets formatting that we don't want the on-screen
     * keyboard to interact with. When the range selects over that formatting, we set this true and
     * then set it to false when the keyboard returns the changes it made so that we can ignore the changes.
     */
    const badSelectionRangeRef = useRef<boolean>(false);
    /**
     * Boolean state to update the email pattern mask once the field has been focused
     * so that it never remasks the value.
     */
    const initialUnmask = useRef<boolean>(false);
    /**
     * Boolean state to update the MUI TextField label's position since it does not always
     * float up when the field has a value.
     */
    const [shrink, setShrink] = useState<boolean>(false);
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    const {
        dealer: { primaryLanguage },
    } = useKioskState();
    const locale = useLocale();

    /**
     * RegExp to validate that the email is in a state where we would mask it.
     */
    const maskEmailRegex: RegExp = useMemo(() => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    }, []);
    /**
     * RegExp to validate that the string of numbers should be treated like a phone number.
     */
    const checkPhoneRegex: RegExp = /^\d{1,10}$/;
    /**
     * RegExp to validate that the string of numbers should still be treated as a phone number when the
     * amount of numbers exceeds the length of a phone number by only one digit.
     */
    const checkPhoneOverflowRegex: RegExp = /^\d{11}$/;

    /**
     * Defines the dynamic mask that is used by react-imask in the hook to control pattern masking for
     * the emails and phone numbers displayed.
     */
    const mask: MaskedDynamicOptions = {
        mask: [
            /**
             * This object handles the masked and unmasked phone number pattern mask.
             */
            {
                mask: initiallyMasked && !initialUnmask.current && !isFocused.current ? '(XXX) XXX-0000' : '(#00) 000-0000',
                definitions: {
                    X: {
                        mask: '0',
                        displayChar: '*',
                        placeholderChar: '#',
                    },
                    '#': /[1-9]/gu,
                },
            },
            /**
             * This object handles all emails that are unmasked.
             */
            {
                mask: /^\S*@?\S*$/,
            },
            /**
             * This object handles all emails that are masked.
             */
            {
                mask: /.*/,
            },
        ],
        // TODO: Look at removing this entirely
        dispatch: (appended: string, dynamicMasked: MaskedDynamic) => {
            const currentIndex: number = dynamicMasked.value.length + appended.length;
            const phoneMask: Masked = dynamicMasked.compiledMasks[0];
            const emailMask: Masked = dynamicMasked.compiledMasks[1];
            const anyMask: Masked = dynamicMasked.compiledMasks[2];
            const combinedValue: string = dynamicMasked.unmaskedValue + appended;

            /**
             * This dispatch returns the phone mask because the phone number only exceeded the length of the phone
             * number by one digit.
             */
            if ((/^\d{1}$/).test(appended) && currentIndex > 14 && (/^\d{1,11}$/gu).test(combinedValue)) {
                return phoneMask;
            }

            /**
             * This dispatch returns the phone mask if the string is only numbers and is between 1-10 numbers in length.
             */
            if ((/^\d{1,10}$/gu).test(combinedValue)) {
                return phoneMask;
            }
            /**
             * This dispatch returns the "anyMask" so that the asterisk masking does not get removed when the
             * email is masked.
             */
            else if (!isFocused.current && initiallyMasked && (/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(combinedValue)) {
                return anyMask;
            }
            /**
             * This dispatch returns the email mask when it is not a phone number and also not a masked email.
             */
            else {
                return emailMask;
            }
        },
    };

    /**
     * This handles the callback to our input element onChange.
     * We have to construct a psuedo change event manually to pass it to the
     * input element onChange.
     */
    const handleAccept = (value: string, masked: InputMask, el: InputEvent | undefined) => {
        const syntheticEvent = {
            ...el,
            target: {
                ...el,
                value: masked.unmaskedValue,
                selectionEnd: masked._selection.end,
            },
        } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

        handleOnChange(syntheticEvent);
    };

    /**
     * This hook handles all the masking and communicates with MUI directly via ref.
     */
    const { value, setValue, maskRef } = useIMask(mask, {
        ref: inputRef,
        onAccept: handleAccept,
    });

    useEffect(() => {
        if (tabFocusRefContext) {
            tabFocusRefContext.current = inputRef.current;
        }
    }, [tabFocusRefContext]);

    /**
     * The dependency array on this is crucial so that when the hook updates MUI but not the value,
     * we still run this useEffect by triggering off of the maskRef changes.
     */
    useEffect(() => {
        if (inputRef.current) {
            /**
             * Updates the internal caret position when the internal MUI value finishes changing from the
             * pattern mask.
             */
            if (caretPositionRef.current) {
                inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);
            }
            /**
             * If for some reason the caret position ref is null, we default to the end of the string.
             */
            else if (!caretPositionRef.current) {
                inputRef.current.setSelectionRange(value.length, value.length);
            }

            /**
             * Set wasOSKInput to false now that we have finished the flow of data between all parts
             * of the control.
             */
            if (wasOSKInput.current === true) {
                wasOSKInput.current = false;
            }
        }
    }, [value, maskRef]);

    useEffect(() => {
        /**
         * When the formik value is not the same as the internal value, we update it to the formik value.
         * We then check to see if the value is empty or null and either update the hook directly via the maskRef
         * or if it is not null or empty update the hook via the setValue function. We also determine whether to
         * mask an email when doing so.
         */
        if (field.value !== valueRef.current) {
            /**
             * Handles correctly masking the email if we need to mask the email before displaying it.
             */
            const maskEmail = (value: string): string => {
                const emailBeforeAt: string = value.substring(0, value.indexOf('@'));
                const emailAfterAt: string = value.substring(value.indexOf('@'));

                return `${emailBeforeAt.slice(0, 2)}******${emailBeforeAt.slice(-2)}${emailAfterAt}`;
            };

            valueRef.current = field.value;

            /**
             * If the string is empty or null, we have to update the hook directly via the updateValue ref
             * funtion and update the unmasked value instead of the value.
             */
            if (maskRef.current && field.value.length === 0) {
                maskRef.current.unmaskedValue = field.value;
                maskRef.current.updateValue();
            }
            /**
             * If the string is not empty, then we can update the hook via the setValue function provided by the
             * hook directly.
             */
            else {
                setShrink(true);
                if (!isFocused.current && initiallyMasked && maskEmailRegex.test(field.value) && inputRef.current) {
                    setValue(maskEmail(field.value));
                }
                else {
                    setValue(field.value);
                }
            }
        }
    }, [field.value, initiallyMasked, maskEmailRegex, maskRef, setValue]);

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const handleGetValue = (): GetValueReturn => {
        /**
         * Set on-screen keyboard input to true to block handleOnChange from re-updating keyboard
         * state values that are updated in the setValue keyboard function.
         */
        wasOSKInput.current = true;

        /**
         * We maintain an internal state of the phone number with no formatting, so
         * we have a caret position map for where the on-screen keyboard should start based
         * on the caret position that MUI gives us with the formatting.
         */
        const convertPhoneMaskedToUnmaskedCaretPosition: NumberMap = {
            0: 0,
            1: 0,
            2: 1,
            3: 2,
            4: 3,
            5: 3,
            6: 3,
            7: 4,
            8: 5,
            9: 6,
            10: 6,
            11: 7,
            12: 8,
            13: 9,
            14: 10,
        };

        /**
         * Since the phone formatting is not passed to the keyboard, we want to prevent a selection range
         * over formatting from interacting with the actual numbers
         */
        if (inputRef.current && checkPhoneRegex.test(valueRef.current)) {
            if (inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
                /**
                 * Handles selecting the `(` at the start of the formatting. We remove the range immediately
                 * and reset the selection range to be at the beginning of the string, so that the curser looks
                 * like it backspaced but did not delete the `(`.
                 */
                if (inputRef.current.selectionStart === 0 && inputRef.current.selectionEnd === 1) {
                    const selection = window.getSelection();
                    if (selection) {
                        selection.removeAllRanges();
                    }
                    inputRef.current.setSelectionRange(inputRef.current.selectionStart, inputRef.current.selectionStart);
                }
                /**
                 * Handles the selection of `) ` in the phone number formatting. We set the ref to true in this case to handle it in the setValue function.
                 */
                if ((inputRef.current.selectionStart >= 4 && inputRef.current.selectionStart <= 5) &&
                    (inputRef.current.selectionEnd >= 5 && inputRef.current.selectionEnd <= 6)) {
                    badSelectionRangeRef.current = true;
                }
                /**
                 * Handles the selection of `-` in the phone number formatting. We set the ref to true in this case to handle it in the setValue function.
                 */
                if (inputRef.current.selectionStart === 9 && inputRef.current.selectionEnd === 10) {
                    badSelectionRangeRef.current = true;
                }
            }
        }

        /**
         * If for some reason the internal value differs from the formik value because the internal
         * value is empty, we update the formik value immdiately to also be empty.
         */
        if (valueRef.current.length === 0 && field.value.length !== 0) {
            setFieldValue(name, '');
        }

        /**
         * Verifies if the value is a phone number.
         */
        const isPhone: boolean = checkPhoneRegex.test(valueRef.current);
        /**
         * If the value is a phone number, use the caret position map to set the starting caret position otherwise
         * use whatever the caret position is set to by MUI.
         */
        const startPosition: number = isPhone ? (
            convertPhoneMaskedToUnmaskedCaretPosition[inputRef.current ? inputRef.current.selectionStart ?? 0 : 0]
        ) : (
            inputRef.current ? inputRef.current.selectionStart ?? 0 : 0
        );
        /**
         * If the value is a phone number, use the caret position map to set the starting caret position otherwise
         * use whatever the caret position is set to by MUI.
         */
        const endPosition: number = isPhone ? (
            convertPhoneMaskedToUnmaskedCaretPosition[inputRef.current ? inputRef.current.selectionEnd ?? 0 : 0]
        ) : (
            inputRef.current ? inputRef.current.selectionEnd ?? 0 : 0
        );

        return {
            inputValue: valueRef.current,
            caretPositionStart: startPosition,
            caretPositionEnd: endPosition,
        };
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const handleSetValue = ({ value, caretPosition }: SetValueProps): void => {
        /**
         * When the on-screen keyboard passes the caret position back to us,
         * we need to convert that position to the correct position with formatting.
         * This map handles the conversion back to the caret position with formatting.
         */
        const convertPhoneUnmaskedToMaskedCaretPosition: NumberMap = {
            0: 1,
            1: 2,
            2: 3,
            3: 4,
            4: 7,
            5: 8,
            6: 9,
            7: 11,
            8: 12,
            9: 13,
            10: 14,
        };

        /**
         * If the selection range was considered to be bad, set the boolean to false and then
         * remove the selection range and update the caret position using to map to one position
         * greater than what it was previously before returning with nothing to end the function.
         *
         * This probably doesn't work if you select a range, but then type a value.
         */
        if (badSelectionRangeRef.current && inputRef.current) {
            badSelectionRangeRef.current = false;
            const selection = window.getSelection();
            if (selection) {
                selection.removeAllRanges();
            }
            inputRef.current.setSelectionRange(
                convertPhoneUnmaskedToMaskedCaretPosition[caretPosition + 1],
                convertPhoneUnmaskedToMaskedCaretPosition[caretPosition + 1],
            );
            return;
        }

        /**
         * If the value returned is a phone number, convert the caret position using the map.
         */
        if (checkPhoneRegex.test(value)) {
            caretPositionRef.current = convertPhoneUnmaskedToMaskedCaretPosition[caretPosition];
        }
        /**
         * If the value is not a phone number, use whatever position was returned by the on-screen
         * keyboard. Then immediately update the MUI position and request an animation frame to update
         * it visually.
         */
        else if (inputRef.current) {
            const element = inputRef.current;
            window.requestAnimationFrame(() => {
                element.selectionStart = caretPosition;
                element.selectionEnd = caretPosition;
            });
            inputRef.current.setSelectionRange(caretPosition, caretPosition);
        }

        onChangeCallback && onChangeCallback();
        valueRef.current = value;
        setFieldValue(name, value);

        /**
         * If the value returned was empty, use the maskRef updateValue function and set the empty
         * value to the refs unmaskedValue rather than use the setValue hook function to ensure the
         * mask updates.
         */
        if (maskRef.current && value.length === 0) {
            maskRef.current.unmaskedValue = value;
            maskRef.current.updateValue();
        }
        /**
         * If the value is not empty, then use setValue to update the mask via the hook's function.
         */
        else {
            setValue(value);
        }
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const handleOnInputChange = ({ value, caretPosition, wasBackspace, wasSelectionRange }: OnInputChangeProps): OnInputChangeReturn => {
        /**
         * If the value given by the keyboard exceeds a phone numbers length by one, remove the new value and
         * set the caret position to one less than what was given.
         */
        if (checkPhoneOverflowRegex.test(value)) {
            value = value.slice(0, caretPosition - 1) + value.slice(caretPosition);
            caretPosition = caretPosition - 1;
        }

        return { value: value,
            caretPosition: caretPosition };
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const handleOnKeyboardInput = (): void => {
        if (inputRef.current && !isFocused.current) {
            inputRef.current.focus();
        }
    };

    const { onFocus, onBlur } = useKeyboardIntegration({
        fieldValue: field.value,
        getValue: handleGetValue,
        setValue: handleSetValue,
        onInputChange: handleOnInputChange,
        onKeyboardInput: handleOnKeyboardInput,
        keyboardLayout: keyboardLayout,
        shiftAfterPeriod: shiftAfterPeriod,
        shiftAfterSpace: shiftAfterSpace,
        shiftAfterAmpersand: shiftAfterAmpersand,
        hideKeyboardOnClickOut: hideKeyboardOnClickOut,
        capsLock: capsLock,
    });

    const handleOnBlur = () => {
        onBlur();
        isFocused.current = false;

        /**
         * If the text field has no internal value, set the shrink to false so that the label
         * floats down into the text field.
         */
        if (inputRef.current && (inputRef.current.value === null || inputRef.current.value === '')) {
            setShrink(false);
        }
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        /**
         * When we use keyboard input, we need to update the internal ref value from the
         * maskRef's unmaskedValue rather than the value which has formatting so that formik,
         * the keyboard, and anything else that relies on the internal value does not break due
         * to the mask formatting.
         */
        if (maskRef.current && wasOSKInput.current !== true) {
            caretPositionRef.current = event.target.selectionEnd;
            onChangeCallback && onChangeCallback();
            const unmaskedValue: string = maskRef.current.unmaskedValue;
            valueRef.current = unmaskedValue;
            setFieldValue(name, unmaskedValue);
        }
    };

    const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        /**
         * When the field is masked as an email or phone, and has not been unmasked yet,
         * manually update the unmaskedValue on the maskRef and then force and update to the mask
         * via the updateValue funciton on the maskRef. Also update the internal ref for this control
         * to be empty.
         */
        if (((maskEmailRegex.test(valueRef.current) && initiallyMasked) || (checkPhoneRegex.test(valueRef.current) && initiallyMasked && !isFocused.current)) &&
            !initialUnmask.current && maskRef.current) {
            maskRef.current.unmaskedValue = '';
            maskRef.current.updateValue();
            valueRef.current = '';
        }
        initialUnmask.current = true;
        isFocused.current = true;
        setShrink(true);
        onFocus(event);
    };

    return (
        <BaseTextField
            name={name}
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            inputMode={'text'}
            type={'text'}
            inputRef={inputRef}
            error={meta.error}
            touched={meta.touched || submitCount > 0}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            capsLock={capsLock}
            onFocus={handleOnFocus}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            InputLabelProps={{
                shrink: shrink,
            }}
            sx={{
                /**
                 * This handles the caret position animation by hiding it when we update it.
                 * Without this, it will flicker on the screen sometimes.
                 */
                caretColor: wasOSKInput.current ? 'transparent' : 'unset',
            }}
        />
    );
},
);